import React, { FunctionComponent, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { CENTER_OF_EXCELLENCE } from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import { getBreakpointValue } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/breakpoints'

import { IS_WEB } from '../../constants'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'
import { ArrowIcon } from '../icons/ArrowIcon'
import { ChevronIconDirection } from '../icons/ChevronIcon'
import { ChevronV2Icon } from '../icons/ChevronV2Icon'
import { NeurodiversityCOEToolkitIcon } from '../icons/NeurodiversityCOEToolkitIcon'
import { Image } from '../image/Image'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'
import { Title } from '../title/Title'

export interface CenterOfExcellenceEntrypointProps {
  centerOfExcellenceType: CENTER_OF_EXCELLENCE
  isUserTeen: boolean
  onPress: () => void
}

const centerOfExcellenceToolkitTitles = defineMessages({
  NEURODIVERSITY: {
    defaultMessage: 'Learn more about neurodiversity support',
    description: 'Title for the neurodiversity center of excellence entrypoint',
  },
})

const centerOfExcellenceToolkitDescriptions = defineMessages({
  NEURODIVERSITY_ADULT: {
    defaultMessage:
      'Expert care for neurodiverse individuals, parents, and children ages 1-17, including neuropsychological evaluations and caregiver coaching.',
    description: 'Description for the neurodiversity center of excellence entrypoint for adult users',
  },
  NEURODIVERSITY_TEEN: {
    defaultMessage: 'Access expert care for neurodiverse needs, consider neuropsychological testing, and more.',
    description: 'Description for the neurodiversity center of excellence entrypoint for teen users',
  },
})

const Container = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.components.centerOfExcellenceEntryPointBackground.background,
  borderRadius: theme.spacing['16px'],
  padding: theme.spacing['16px'],
  flexDirection: 'row',
}))

const TextAndIconContainer = styled.View(({ theme }) => ({
  flex: 1,
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  alignItems: 'center',
  gap: theme.breakpoints.isMinWidthTablet ? theme.spacing['16px'] : '10px',
}))

const TextContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['8px'],
  maxWidth: '728px',
  ...(theme.breakpoints.isMinWidthTablet && { flex: 1 }),
}))

const IconContainer = styled.View(({ theme }) => ({
  height: '63px',
  width: '63px',
  ...(theme.breakpoints.isMobileSized && { width: '100%' }),
}))

const ArrowContainer = styled.View({
  justifyContent: 'center',
  alignItems: 'flex-end',
})

const AuroraPressableOpacity = styled(PressableOpacity)({
  maxWidth: '944px',
})

const AuroraContainer = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.cardBackgroundPrimary,
  borderRadius: theme.spacing['24px'],
  flexDirection: theme.breakpoints.isMobileSized ? 'column' : 'row',
  overflow: 'hidden',
  maxWidth: '944px',
}))

const AuroraImageContainer = styled.View(({ theme }) => ({
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
  ...getBreakpointValue(theme.breakpoints, {
    isMobileSized: { width: '100%', height: '180px' },
    default: { width: theme.breakpoints.isMinWidthTablet ? '304px' : '200px', minHeight: '144px' },
  }),
}))

const AuroraContentContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  padding: theme.spacing['24px'],
  ...(!theme.breakpoints.isMobileSized && { flex: 1 }),
}))

const AuroraTextContainer = styled.View(({ theme }) => ({
  flex: 1,
  flexDirection: 'column',
  gap: theme.spacing['8px'],
}))

const AuroraChevronContainer = styled.View({
  justifyContent: 'center',
  alignItems: 'center',
})

const AuroraImageFill = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
})

const AuroraStyledImage = styled(Image)({
  width: '100%',
  height: '100%',
  ...(IS_WEB && { objectFit: 'cover' }),
})

export const CenterOfExcellenceEntrypoint: FunctionComponent<CenterOfExcellenceEntrypointProps> = ({
  centerOfExcellenceType,
  isUserTeen,
  onPress,
}) => {
  const { assets, breakpoints, colors, isAuroraTheme } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const CENTER_OF_EXCELLENCE_TOOLKIT_TO_CONTENT = useMemo(
    () => ({
      [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: {
        title: centerOfExcellenceToolkitTitles.NEURODIVERSITY,
        description: isUserTeen
          ? centerOfExcellenceToolkitDescriptions.NEURODIVERSITY_TEEN
          : centerOfExcellenceToolkitDescriptions.NEURODIVERSITY_ADULT,
        getIcon: (size?: number) => <NeurodiversityCOEToolkitIcon size={size} />,
        image: isUserTeen ? assets.neurodiversityEntrypointBannerTeen : assets.neurodiversityEntrypointBannerAdult,
      },
    }),
    [assets.neurodiversityEntrypointBannerAdult, assets.neurodiversityEntrypointBannerTeen, isUserTeen],
  )
  const { title, description, getIcon, image } = CENTER_OF_EXCELLENCE_TOOLKIT_TO_CONTENT[centerOfExcellenceType]

  const imageUrl = useGetResponsiveAssets(image?.assetNames ?? { defaultAsset: '' })

  if (isAuroraTheme) {
    return (
      <AuroraPressableOpacity
        onPress={onPress}
        testID={tID('CenterOfExcellenceEntrypoint')}
        accessibilityLabel={formatMessage(title)}
      >
        <AuroraContainer>
          <AuroraImageContainer>
            {imageUrl ? (
              <AuroraImageFill>
                <AuroraStyledImage
                  source={{ uri: imageUrl }}
                  alt={formatMessage({
                    defaultMessage: 'Neurodiversity support',
                    description: 'Alt text for neurodiversity entrypoint banner image',
                  })}
                  contentFit='cover'
                />
              </AuroraImageFill>
            ) : null}
          </AuroraImageContainer>
          <AuroraContentContainer>
            <AuroraTextContainer>
              <Title
                size={breakpoints.isMinWidthTablet ? TitleSize.MEDIUM : TitleSize.SMALL}
                text={formatMessage(title)}
                color={colors.textPrimary}
                numberOfLines={breakpoints.isMinWidthTablet ? 2 : 4}
              />
              <BodyText text={formatMessage(description)} color={colors.textSecondary} />
            </AuroraTextContainer>
            <AuroraChevronContainer>
              <ChevronV2Icon direction={ChevronIconDirection.RIGHT} fillColor={colors.textPrimary} size={12} />
            </AuroraChevronContainer>
          </AuroraContentContainer>
        </AuroraContainer>
      </AuroraPressableOpacity>
    )
  }

  const iconComponent = getIcon()
  return (
    <PressableOpacity onPress={onPress} testID={tID('CenterOfExcellenceEntrypoint')}>
      <Container>
        <TextAndIconContainer>
          <IconContainer>{iconComponent}</IconContainer>
          <TextContainer>
            <Title
              size={TitleSize.MEDIUM}
              text={formatMessage(title)}
              numberOfLines={breakpoints.isMinWidthTablet ? 2 : 4}
            />
            <BodyText text={formatMessage(description)} />
          </TextContainer>
        </TextAndIconContainer>
        <ArrowContainer>
          <ArrowIcon fillColor={colors.textSecondary} />
        </ArrowContainer>
      </Container>
    </PressableOpacity>
  )
}
